
import { Button } from "@/components/ui/button"
import { ThemeToggle } from "@/components/theme-toggle"
import { LanguageToggle } from "@/components/language-toggle"
import { CommandSearch } from "@/components/search/command-search"
import { Menu, Gamepad2 } from "lucide-react"
import { useLocation } from "react-router-dom"
import { useTranslation } from "@/hooks/use-translation"
import { getGameByLocalizedId } from "@/lib/games-data"

interface HeaderProps {
  sidebarCollapsed: boolean
  setSidebarCollapsed: (collapsed: boolean) => void
}

export function Header({ sidebarCollapsed, setSidebarCollapsed }: HeaderProps) {
  const location = useLocation()
  const { t, language } = useTranslation()

  // Detectar el juego actual desde la URL
  const pathSegments = location.pathname.split('/').filter(Boolean)
  const langSegment = pathSegments[0] || ''
  const isLanguageInUrl = ['en', 'es', 'fr'].includes(langSegment)

  const localizedGameId = isLanguageInUrl
    ? (pathSegments[1] === 'game' ? pathSegments[2] : null)
    : (pathSegments[0] === 'game' ? pathSegments[1] : null)

  const currentGame = localizedGameId ? getGameByLocalizedId(localizedGameId, language) : null

  // Detectar la página actual para mostrar el título en móvil
  const getCurrentPageTitle = () => {
    const path = location.pathname

    // Home page
    if (path === '/' || path === `/${language}`) {
      return t("navigation.home")
    }

    // Statistics page
    if (path === '/statistics' || path === `/${language}/statistics`) {
      return t("navigation.statistics")
    }

    // Category page
    if (path.includes('/category/')) {
      const categoryId = isLanguageInUrl ? pathSegments[2] : pathSegments[1]
      return t(`categories.${categoryId}.name`)
    }

    // Game page
    if (currentGame) {
      return t(`games.${currentGame.id}.name`)
    }

    return null
  }

  const currentPageTitle = getCurrentPageTitle()

  return (
    <header className="h-16 border-b bg-background flex items-center justify-between px-4 flex-shrink-0">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          aria-label="Toggle navigation menu"
        >
          <Menu className="h-5 w-5" />
          <span className="sr-only">Toggle navigation menu</span>
        </Button>

        {/* Logo y título - Solo en móvil */}
        <div className="flex items-center gap-2 lg:hidden">
          <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
            <Gamepad2 className="h-5 w-5 text-primary" />
          </div>
          <h1 className="text-lg font-semibold hidden sm:block">
            {t("app.title")}
          </h1>
        </div>
      </div>

      <div className="flex items-center gap-2 max-w-md flex-1 mx-4">
        {/* En móvil mostrar título del juego o página, en desktop mostrar search */}
        {currentPageTitle ? (
          <div className="lg:hidden text-center flex-1">
            <h1 className="text-lg font-semibold text-foreground truncate">
              {currentPageTitle}
            </h1>
          </div>
        ) : null}
        <div className="hidden lg:block w-full">
          <CommandSearch />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <LanguageToggle />
        <ThemeToggle />
      </div>
    </header>
  )
}
